import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { components } from "../../../apis/gen";
import { calculateSessionScores } from "../../../utils/score-utils";

import type { EventStatus } from "../types";

type Session = components["schemas"]["Session"];

interface GameCardProps {
	session: Session;
	idx: number;
	status: EventStatus;
	eventId: string;
}

export const GameCard = ({ session, idx, status, eventId }: GameCardProps) => {
	const { t } = useTranslation();
	const sessionStart = new Date(session.started_at);
	const sessionEnd = new Date(session.ended_at);
	const startTime = sessionStart.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
	const endTime = sessionEnd.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});

	const renderSessionContent = (): React.ReactNode => {
		if (status === "upcoming") {
			return (
				<div className="mt-3 flex items-center justify-between gap-x-4 h-14">
					<p className="flex-1 text-xs text-color-Grey500">
						{t("events.not_started")}
					</p>
					<span className="block w-px h-full bg-color-Grey100"></span>
					<Link
						to={`/events/${eventId}#${idx + 1}`}
						className="py-2 px-4 inline-block text-xs bg-color-Grey50 text-color-Grey900 rounded-full"
					>
						{t("events.view_details")}
					</Link>
				</div>
			);
		}

		if (status === "cancelled") {
			return (
				<div className="mt-3 flex items-center justify-between gap-x-4 h-14">
					<p className="flex-1 text-xs text-color-Grey500">
						{t("events.status.cancelled")}
					</p>
				</div>
			);
		}

		if (status === "past") {
			const scores = calculateSessionScores(session.result_details);
			const teamMapping = session.team_mapping;
			const teamAName = teamMapping?.["0"] || "Team A";
			const teamBName = teamMapping?.["1"] || "Team B";
			return (
				<div className="mt-3 flex items-center justify-between gap-x-4 h-14">
					<div className="flex-1">
						<div className="flex flex-col gap-2 text-xs">
							<div className="flex items-center gap-x-2">
								<div className="flex items-center justify-center w-5 h-5 rounded-xl overflow-hidden bg-white">
									<img
										src="/img/event-page/team_light.svg"
										alt={teamAName}
										className="w-5 h-5"
									/>
								</div>
								<span className="text-base font-medium text-color-Grey900">
									{teamAName}
								</span>
								<span className="ml-auto text-lg font-semibold">
									{scores.team0}
								</span>
							</div>
							<div className="flex items-center gap-x-2">
								<div className="flex items-center justify-center w-5 h-5 rounded-xl overflow-hidden bg-white">
									<img
										src="/img/event-page/team_dark.svg"
										alt={teamBName}
										className="w-5 h-5"
									/>
								</div>
								<span className="text-base font-medium text-color-Grey900">
									{teamBName}
								</span>
								<span className="ml-auto text-lg font-semibold">
									{scores.team1}
								</span>
							</div>
						</div>
					</div>
					<span className="block w-px h-full bg-color-Grey100"></span>
					<Link
						to={`/events/${eventId}#${idx + 1}`}
						className="py-2 px-4 inline-block text-xs bg-color-Grey50 text-color-Grey900 rounded-full"
					>
						{t("events.view_details")}
					</Link>
				</div>
			);
		}

		return null;
	};

	return (
		<div className="border border-color-Grey100 rounded-lg bg-color-White p-3">
			<p className="text-base text-color-Grey700">
				{session.name || t("events.session_fallback", { number: idx + 1 })}{" "}
				{startTime}-{endTime}
			</p>
			{renderSessionContent()}
		</div>
	);
};
